<template>
  <section class="lead-flow-timeline">
    <header class="lead-flow-section-heading">
      <strong>Lead flow</strong>
      <span>{{ periods.length }} {{ periods.length === 1 ? 'stage' : 'stages' }}</span>
    </header>

    <div v-if="loading && !periods.length" class="lead-flow-timeline__skeleton" aria-label="Loading workflow history">
      <span v-for="index in 3" :key="index"></span>
    </div>
    <div v-else-if="error && !periods.length" class="lead-flow-timeline__state" role="alert">
      <p>Unable to load workflow history.</p>
      <button type="button" @click="load(true)">Retry</button>
    </div>
    <div v-else-if="periods.length" class="lead-flow-timeline__list">
      <article
        v-for="period in periods"
        :key="period.id"
        class="lead-flow-timeline__period"
      >
        <div class="lead-flow-timeline__rail">
          <span :style="{ backgroundColor: stageColor(period.stage) }"></span>
        </div>
        <div class="lead-flow-timeline__content">
          <div class="lead-flow-timeline__period-heading">
            <AppStatusBadge
              class="lead-flow-timeline__stage"
              design="new"
              :color="stageColor(period.stage)"
              :label="stageName(period.stage)"
            />
            <time
              v-tooltip="{ content: formatExactDate(period.entered_at), placement: 'top', offset: 8 }"
            >
              {{ formatRelativeDate(period.entered_at) }}
            </time>
          </div>
          <p v-if="period.current" class="lead-flow-timeline__current">Current stage</p>
          <ul v-if="period.events && period.events.length" class="lead-flow-timeline__events">
            <li v-for="event in period.events" :key="event.id">
              <i></i>
              <div>
                <p>
                  <strong>{{ eventName(event) }}</strong>
                  <span v-if="event.actor">by {{ actorName(event.actor) }}</span>
                </p>
                <span v-if="eventReasonName(event)" class="lead-flow-timeline__reason">
                  {{ eventReasonName(event) }}
                </span>
                <span v-if="event.origin && event.origin.source" class="lead-flow-timeline__reason">
                  {{ event.origin.source.name }}
                  <a v-if="event.origin.external_object && event.origin.external_object.type === 'trengo.ticket'" :href="`https://app.trengo.com/admin/#/tickets/${event.origin.external_object.id}`" target="_blank" rel="noopener noreferrer">#{{ event.origin.external_object.id }} ↗</a>
                </span>
                <span v-if="event.decision" class="lead-flow-timeline__reason">
                  {{ valueName(event.decision) }}
                </span>
                <span v-if="event.skipped_stage" class="lead-flow-timeline__reason">
                  {{ valueName(event.skipped_stage) }} skipped
                </span>
                <AppStatusBadge
                  v-if="event.communication_method"
                  design="new"
                  tone="neutral"
                  compact
                  class="lead-flow-timeline__method"
                  :label="valueName(event.communication_method)"
                />
                <div
                  v-if="displayNote(event) || hasDeliveries(event)"
                  class="lead-flow-timeline__event-summary"
                >
                  <p v-if="displayNote(event)" class="lead-flow-timeline__note">
                    {{ displayNote(event) }}
                  </p>
                  <button
                    v-if="hasDeliveries(event)"
                    class="lead-flow-timeline__details-toggle"
                    type="button"
                    :aria-expanded="isDeliveriesExpanded(event.id) ? 'true' : 'false'"
                    :aria-controls="`lead-flow-deliveries-${event.id}`"
                    @click="toggleDeliveries(event.id)"
                  >
                    {{ isDeliveriesExpanded(event.id) ? 'Hide' : 'Details' }}
                  </button>
                </div>
                <div
                  v-if="hasDeliveries(event) && isDeliveriesExpanded(event.id)"
                  :id="`lead-flow-deliveries-${event.id}`"
                  class="lead-flow-timeline__delivery-list"
                >
                  <div
                    v-for="(delivery, deliveryIndex) in event.deliveries"
                    :key="`${delivery.provider}-detail-${deliveryIndex}`"
                    class="lead-flow-timeline__delivery"
                    :class="`lead-flow-timeline__delivery--${deliveryTone(delivery)}`"
                  >
                    <span class="lead-flow-timeline__delivery-icon" aria-hidden="true">
                      {{ deliveryIcon(delivery) }}
                    </span>
                    <div class="lead-flow-timeline__delivery-content">
                      <strong>
                        {{ deliveryProviderName(delivery) }} · {{ deliveryStatusName(delivery) }}
                      </strong>
                      <p v-if="deliveryMessage(delivery)">
                        {{ deliveryMessage(delivery) }}
                      </p>
                      <small v-if="delivery.reason">
                        <b>Reason</b> · <code>{{ delivery.reason }}</code>
                      </small>
                      <small v-if="delivery.template">
                        <b>Template</b> · {{ delivery.template }}
                      </small>
                      <small v-if="delivery.language_selection && delivery.language_selection.fallback">
                        <b>Language fallback</b> · requested {{ delivery.language_selection.requested }}, English template selected ({{ delivery.language_selection.selected }})
                      </small>
                    </div>
                  </div>
                </div>
                <time
                  v-tooltip="{ content: formatExactDate(event.occurred_at), placement: 'top', offset: 8 }"
                >
                  {{ formatRelativeDate(event.occurred_at) }}
                </time>
              </div>
            </li>
          </ul>
        </div>
      </article>

      <button
        v-if="hasMore"
        class="lead-flow-timeline__more"
        type="button"
        :disabled="loadingMore"
        @click="load(false)"
      >
        {{ loadingMore ? 'Loading...' : 'Show older activity' }}
      </button>
      <p v-if="error" class="lead-flow-timeline__append-error">
        Loading failed.
        <button type="button" @click="load(false)">Retry</button>
      </p>
    </div>
    <p v-else class="lead-flow-timeline__empty">No workflow history yet</p>
  </section>
</template>

<script>
import { getLeadFlowTimeline } from '@/http'
import { formatExactDate, formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'
import {
  deliveryMessage,
  deliveryIcon,
  deliveryProviderName,
  deliveryStatusName,
  deliveryTone,
  eventName,
  eventNote,
  eventReasonName
} from '@/views/crm/task-tracker/leadFlowTimelinePresentation'

export default {
  name: 'LeadFlowTimeline',
  props: {
    leadId: {
      type: Number,
      required: true
    },
    refreshKey: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      periods: [],
      nextCursor: null,
      hasMore: false,
      loading: false,
      loadingMore: false,
      error: null,
      requestGeneration: 0,
      expandedDeliveries: {}
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.load(true)
      }
    },
    refreshKey() {
      this.load(true)
    }
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    deliveryMessage,
    deliveryIcon,
    deliveryProviderName,
    deliveryStatusName,
    deliveryTone,
    eventReasonName,
    formatExactDate,
    formatRelativeDate,
    async load(reset) {
      if (reset ? this.loading : this.loadingMore) return
      if (!reset && (!this.hasMore || !this.nextCursor)) return

      const leadId = this.leadId
      const generation = reset ? this.requestGeneration + 1 : this.requestGeneration
      this.requestGeneration = generation
      this.loading = reset
      this.loadingMore = !reset
      this.error = null
      try {
        const params = { page_size: 10 }
        if (!reset) params.cursor = this.nextCursor
        const { data } = await getLeadFlowTimeline(leadId, params)
        if (generation !== this.requestGeneration || leadId !== this.leadId) return

        const incoming = Array.isArray(data.results) ? data.results : []
        this.periods = this.mergePeriods(incoming, reset)
        this.nextCursor = data.next_cursor || null
        this.hasMore = Boolean(data.has_more && this.nextCursor)
      } catch (error) {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.error = error
        }
      } finally {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.loading = false
          this.loadingMore = false
        }
      }
    },
    mergePeriods(incoming, reset) {
      const current = reset ? [] : this.periods
      const seen = new Set(current.map(period => period.id))
      return current.concat(incoming.filter(period => {
        if (!period || seen.has(period.id)) return false
        seen.add(period.id)
        return true
      }))
    },
    stageName(stage) {
      return stage && stage.name ? stage.name : 'Without stage'
    },
    stageColor(stage) {
      return stage && stage.color ? stage.color : '#879392'
    },
    eventName(event) {
      return eventName(event) || 'Workflow event'
    },
    displayNote(event) {
      return eventNote(event)
    },
    hasDeliveries(event) {
      return Boolean(event && Array.isArray(event.deliveries) && event.deliveries.length)
    },
    isDeliveriesExpanded(eventId) {
      return Boolean(this.expandedDeliveries[eventId])
    },
    toggleDeliveries(eventId) {
      this.$set(
        this.expandedDeliveries,
        eventId,
        !this.isDeliveriesExpanded(eventId)
      )
    },
    actorName(actor) {
      return actor.name || actor.full_name || 'Team member'
    },
    valueName(value) {
      return value && (value.name || value.label || value.code) ? (value.name || value.label || value.code) : ''
    },
    reset() {
      this.requestGeneration += 1
      this.periods = []
      this.nextCursor = null
      this.hasMore = false
      this.loading = false
      this.loadingMore = false
      this.error = null
      this.expandedDeliveries = {}
    }
  }
}
</script>

<style scoped>
.lead-flow-timeline {
  min-width: 0;
  padding: 16px;
  border: 1px solid #dfe4dd;
  border-radius: 7px;
  background: #fff;
}

.lead-flow-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lead-flow-section-heading strong {
  color: #657574;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-flow-section-heading span {
  color: #929d9b;
  font-size: 10px;
}

.lead-flow-timeline__list {
  margin-top: 15px;
}

.lead-flow-timeline__period {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
}

.lead-flow-timeline__rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.lead-flow-timeline__rail::after {
  position: absolute;
  top: 14px;
  bottom: -15px;
  width: 1px;
  background: #dfe4dd;
  content: '';
}

.lead-flow-timeline__period:last-of-type .lead-flow-timeline__rail::after {
  display: none;
}

.lead-flow-timeline__rail span {
  z-index: 1;
  width: 9px;
  height: 9px;
  margin-top: 8px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d7ddd5;
}

.lead-flow-timeline__content {
  min-width: 0;
  padding: 0 0 22px 8px;
}

.lead-flow-timeline__period-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
}

.lead-flow-timeline__stage {
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid;
  border-radius: 11px;
  font-size: 9px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-timeline time {
  color: #98a2a0;
  font-size: 9px;
  white-space: nowrap;
}

.lead-flow-timeline__current {
  margin: 5px 0 0;
  color: #659b1d;
  font-size: 9px;
  font-weight: 700;
}

.lead-flow-timeline__events {
  display: grid;
  gap: 10px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.lead-flow-timeline__events li {
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr);
  gap: 7px;
}

.lead-flow-timeline__events i {
  width: 5px;
  height: 5px;
  margin-top: 5px;
  border-radius: 50%;
  background: #aab5af;
}

.lead-flow-timeline__events p {
  margin: 0;
  color: #506062;
  font-size: 10px;
  line-height: 15px;
}

.lead-flow-timeline__events p span {
  margin-left: 4px;
  color: #899593;
}

.lead-flow-timeline__events > li > div > span {
  display: inline-block;
  margin: 4px 5px 0 0;
  color: #74817f;
  font-size: 9px;
}

.lead-flow-timeline__method {
  padding: 2px 5px;
  border-radius: 8px;
  background: #eef3eb;
}

.lead-flow-timeline__note {
  margin-top: 4px !important;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.lead-flow-timeline__event-summary {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.lead-flow-timeline__event-summary .lead-flow-timeline__note {
  min-width: 0;
  flex: 1;
}

.lead-flow-timeline__details-toggle {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3477d8;
  font-size: 9px;
  font-weight: 700;
  line-height: 15px;
  cursor: pointer;
}

.lead-flow-timeline__delivery-list {
  display: grid;
  margin: 5px 0 3px;
}

.lead-flow-timeline__delivery {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 7px;
  padding: 7px 0;
  border-top: 1px solid #e4e9e3;
}

.lead-flow-timeline__delivery:last-child {
  border-bottom: 1px solid #e4e9e3;
}

.lead-flow-timeline__delivery-icon {
  display: inline-flex !important;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  color: #71807f !important;
  font-size: 16px !important;
  font-weight: 700;
  line-height: 1;
}

.lead-flow-timeline__delivery--blue .lead-flow-timeline__delivery-icon {
  color: #1769e0 !important;
}

.lead-flow-timeline__delivery--green .lead-flow-timeline__delivery-icon {
  color: #1b8f65 !important;
}

.lead-flow-timeline__delivery--amber .lead-flow-timeline__delivery-icon {
  color: #a96a00 !important;
}

.lead-flow-timeline__delivery--red .lead-flow-timeline__delivery-icon {
  color: #c53b4b !important;
}

.lead-flow-timeline__delivery-content {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.lead-flow-timeline__delivery-content > strong {
  color: #344748;
  font-size: 10px;
  line-height: 15px;
}

.lead-flow-timeline__delivery-content p,
.lead-flow-timeline__delivery-content small {
  margin: 0;
  color: #667574;
  font-size: 9px;
  line-height: 15px;
  overflow-wrap: anywhere;
}

.lead-flow-timeline__delivery-content small {
  display: block;
}

.lead-flow-timeline__delivery-content small b {
  color: #71807f;
  font-weight: 700;
}

.lead-flow-timeline__delivery code {
  color: #657574;
  font-family: inherit;
}

.lead-flow-timeline__more {
  width: 100%;
  min-height: 32px;
  border: 1px solid #d7e2cb;
  border-radius: 5px;
  background: #f8fbf5;
  color: #5c8d20;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-flow-timeline__more:disabled {
  cursor: wait;
  opacity: .6;
}

.lead-flow-timeline__empty,
.lead-flow-timeline__state,
.lead-flow-timeline__append-error {
  margin: 15px 0 0;
  color: #8a9695;
  font-size: 11px;
}

.lead-flow-timeline__state button,
.lead-flow-timeline__append-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #55940a;
  font-weight: 700;
  cursor: pointer;
}

.lead-flow-timeline__append-error {
  color: #b85049;
  text-align: center;
}

.lead-flow-timeline__skeleton {
  display: grid;
  gap: 12px;
  margin-top: 15px;
}

.lead-flow-timeline__skeleton span {
  height: 54px;
  border-radius: 5px;
  background: #eef1ec;
}
</style>
